import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IMAGE, VIDEO } from 'constants/media-types.constants';
import { BASE_URL } from 'constants/env';
import styles from './MediaDiscoveryItem.module.css';
import { useMediaUser } from 'utils/use-media-user';
import { DownloadOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import dayjs from 'dayjs';
import { MEDIA_TYPE_QUERY_PARAM } from 'hooks/useMediaTypeQuery';
import { MediaSchema } from 'api/trip-media';

interface MediaDiscoveryItemProps {
    media: PSMedia | MediaSchema;
    hasTripLink?: boolean;
}

export function MediaDiscoveryItem({ media, hasTripLink = true }: MediaDiscoveryItemProps) {
    const [isHovering, setIsHovering] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const preview = useMemo(() => {
        if (media.type === IMAGE || !isHovering) {
            return <img className={styles.preview} src={media.large_thumbnail_path} loading='lazy' />;
        }

        if (media.type === VIDEO) {
            return <video ref={videoRef} className={styles.preview} autoPlay muted controls src={media.cdn_path} />;
        }

        return <div className={styles.preview}>Wrong media type</div>;
    }, [media, isHovering]);
    // isHovered is used to get the user for the media only once the user has hovered over the media
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        if (isHovering) {
            videoRef.current?.play();
        } else {
            videoRef.current?.pause();
        }
    }, [isHovering]);
    const { user } = useMediaUser(isHovered ? media : null);

    const mediaTypeParam = media.type === VIDEO ? 'video' : 'photo';

    return (
        <div
            className={styles.root}
            onMouseEnter={() => {
                setIsHovered(true);
                setIsHovering(true);
            }}
            onMouseLeave={() => {
                setIsHovering(false);
            }}>
            {preview}
            <div className={styles.tools}>
                {user ? (
                    <div className={styles.tools__userInfo}>
                        <div>
                            {media.type === IMAGE ? 'Photo' : 'Video'} from{' '}
                            <a href={`${BASE_URL}/${user.username}`} target='_blank' rel='noreferrer'>
                                {user.username}
                            </a>
                        </div>
                        <a
                            href={`${BASE_URL}/${user.username}/${media.step?.trip_id}-unkown-name/${media.step?.id}-unknown-name`}
                            target='_blank'
                            rel='noreferrer'>
                            View step
                        </a>
                        {hasTripLink ? (
                            // New tab to avoid losing the current page state, as that's not representated in the route url
                            <a
                                href={`/media-discovery/trips/${media.step?.trip_id}?${MEDIA_TYPE_QUERY_PARAM}=${mediaTypeParam}`}
                                target='_blank'
                                rel='noreferrer'>
                                View trip media
                            </a>
                        ) : null}
                        {media.step ? <div>{dayjs(media.step.start_time).format('DD MMM YYYY')}</div> : null}
                    </div>
                ) : null}
                <Button
                    type='primary'
                    icon={<DownloadOutlined />}
                    size='small'
                    target='_blank'
                    href={media.type === IMAGE ? media.large_thumbnail_path : media.cdn_path}
                />
            </div>
        </div>
    );
}
